import { Component, Input, OnDestroy, OnInit } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { Subscription } from 'rxjs';
import { IDrawing } from 'lightweight-charts-drawing';
import { ChartDrawingController } from '../chart-drawing-controller';
import { getToolLabel, getToolMeta, ToolUiMeta, TV_PALETTE } from '../drawing-tools.catalog';
import { TemaService } from 'src/app/services/tema.services';
import { DrawingSettingsDialogComponent } from '../drawing-settings-dialog/drawing-settings-dialog.component';

const LINE_WIDTHS = [1, 2, 3, 4];

@Component({
  selector: 'app-drawing-floating-toolbar',
  templateUrl: './drawing-floating-toolbar.component.html',
  styleUrls: ['./drawing-floating-toolbar.component.scss'],
})
export class DrawingFloatingToolbarComponent implements OnInit, OnDestroy {
  @Input() controller!: ChartDrawingController;

  public palette = TV_PALETTE;
  public lineWidths = LINE_WIDTHS;
  public selection: IDrawing | null = null;
  public meta: ToolUiMeta | undefined;
  public isDark = false;
  public posLeft = 0;
  public posTop = 0;
  public panel: 'line-color' | 'fill-color' | 'width' | 'style' | 'template' | null = null;

  private subs: Subscription[] = [];

  constructor(private temaService: TemaService, private dialog: MatDialog) {}

  ngOnInit(): void {
    this.subs.push(
      this.temaService.valDarkTheme.subscribe(v => (this.isDark = v)),
      this.controller.selection$.subscribe(d => {
        this.selection = d;
        this.meta = d ? getToolMeta(d.type) : undefined;
        this.panel = null;
      }),
      this.controller.selectionScreenBox$.subscribe(box => {
        if (!box) return;
        // encima del bounding box del dibujo, sin salirse del contenedor
        this.posLeft = Math.max(8, (box.left + box.right) / 2 - 160);
        this.posTop = Math.max(8, box.top - 48);
      }),
    );
  }

  ngOnDestroy(): void {
    this.subs.forEach(s => s.unsubscribe());
  }

  public get toolLabel(): string {
    return this.selection ? getToolLabel(this.selection.type) : '';
  }

  public togglePanel(panel: NonNullable<DrawingFloatingToolbarComponent['panel']>): void {
    this.panel = this.panel === panel ? null : panel;
  }

  // ── estilo ───────────────────────────────────────────────────────────

  public get lineColor(): string { return this.selection?.style.lineColor ?? '#2962FF'; }
  public get fillColor(): string { return this.selection?.style.fillColor ?? this.lineColor; }
  public get lineWidth(): number { return this.selection?.style.lineWidth ?? 2; }

  public setLineColor(color: string): void {
    if (!this.selection) return;
    this.controller.updateStyle(this.selection.id, { lineColor: color });
    this.controller.defaultStyle.lineColor = color;
    this.panel = null;
  }

  public setFillColor(color: string): void {
    if (!this.selection) return;
    this.controller.updateStyle(this.selection.id, { fillColor: color });
    this.panel = null;
  }

  public setLineWidth(width: number): void {
    if (!this.selection) return;
    this.controller.updateStyle(this.selection.id, { lineWidth: width });
    this.controller.defaultStyle.lineWidth = width;
    this.panel = null;
  }

  public setLineDash(dash: number[]): void {
    if (!this.selection) return;
    this.controller.updateStyle(this.selection.id, { lineDash: dash });
    this.panel = null;
  }

  public isDashActive(dash: number[]): boolean {
    const current = this.selection?.style.lineDash ?? [];
    return JSON.stringify(current) === JSON.stringify(dash);
  }

  // ── plantillas ───────────────────────────────────────────────────────

  public get hasTemplate(): boolean {
    return !!this.selection && !!this.controller.loadTemplate(this.selection.type);
  }

  public saveAsTemplate(): void {
    if (this.selection) this.controller.saveTemplate(this.selection);
    this.panel = null;
  }

  public applyTemplate(): void {
    if (!this.selection) return;
    const tpl = this.controller.loadTemplate(this.selection.type);
    if (tpl?.style) this.controller.updateStyle(this.selection.id, tpl.style);
    if (tpl?.options) this.controller.updateOptions(this.selection.id, tpl.options);
    this.panel = null;
  }

  public clearTemplate(): void {
    if (this.selection) this.controller.clearTemplate(this.selection.type);
    this.panel = null;
  }

  // ── acciones ─────────────────────────────────────────────────────────

  public get isLocked(): boolean { return this.selection?.options.locked === true; }
  public get isVisible(): boolean { return this.selection?.options.visible !== false; }

  public toggleLock(): void {
    if (this.selection) this.controller.toggleLock(this.selection.id);
  }

  public toggleVisible(): void {
    if (this.selection) this.controller.toggleVisible(this.selection.id);
  }

  public clone(): void {
    this.controller.cloneSelected();
  }

  public openSettings(): void {
    if (!this.selection) return;
    this.dialog.open(DrawingSettingsDialogComponent, {
      data: { controller: this.controller, drawingId: this.selection.id },
      autoFocus: false,
    });
  }

  public remove(): void {
    this.controller.deleteSelected();
  }
}
